import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabaseClient';
import '@google/model-viewer';

const FoodViewer = ({ modelSrc, imageSrc, itemName, itemId, restaurantId, themeColor = '#ee5b2b' }) => {
  const [isActive, setIsActive] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const modelRef = useRef(null);

  // --- TRACKING FUNCTION ---
  const handleEnable3D = async () => {
    setIsActive(true); // Enable 3D Mode immediately

    // Send tracking event to Supabase
    if (restaurantId && itemId) {
      try {
        await supabase.from('analytics_events').insert([
          { 
            restaurant_id: restaurantId,
            event_type: 'ar_view', 
            item_id: itemId
          }
        ]);
      } catch (err) {
        console.error("Tracking Failed:", err);
      }
    }
  };

  useEffect(() => {
    if (isActive && modelRef.current) {
      const model = modelRef.current;
      const handleLoad = () => setIsLoaded(true);
      
      // Wait for model to be visual ready
      model.addEventListener('load', handleLoad);
      model.addEventListener('poster-dismissed', handleLoad);
      
      if (model.loaded) setIsLoaded(true);
      
      return () => {
        model.removeEventListener('load', handleLoad);
        model.removeEventListener('poster-dismissed', handleLoad);
      };
    }
  }, [isActive, modelSrc]);

  return (
    <div 
      className="relative w-full h-full bg-gray-50 group cursor-pointer" 
      onClick={handleEnable3D} 
    >
      
      {/* 1. IDLE STATE: Image Overlay */}
      {!isActive ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <img
            src={imageSrc || "https://via.placeholder.com/400x400?text=No+Image"} 
            alt={itemName}
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-black/10 transition-opacity group-hover:opacity-20"></div>
          
          {/* Overlay Button */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full flex items-center gap-2 shadow-sm transition-transform group-hover:scale-105">
            <span className="text-xl">📦</span>
            <span className="font-bold text-gray-900 text-sm">View in 3D</span>
          </div>
        </div>
      ) : (
        /* 2. ACTIVE STATE: 3D Model */
        <>
          {/* Dynamic Loading Spinner */}
          <div className={`absolute inset-0 flex items-center justify-center bg-white z-10 transition-opacity duration-500 pointer-events-none ${isLoaded ? 'opacity-0' : 'opacity-100'}`}>
            <div 
              className="w-8 h-8 border-4 border-t-transparent rounded-full animate-spin"
              style={{ borderColor: themeColor, borderTopColor: 'transparent' }}
            ></div>
          </div>

          <model-viewer
            ref={modelRef}
            src={modelSrc}
            alt={itemName}
            ar
            ar-modes="scene-viewer webxr quick-look"
            camera-controls
            auto-rotate
            shadow-intensity="1"
            style={{ width: '100%', height: '100%' }}
          >
            <button slot="ar-button" className="hidden"></button>
          </model-viewer>

          {/* Close Button */}
          <button
            onClick={(e) => { e.stopPropagation(); setIsActive(false); }}
            className="absolute top-2 right-2 z-20 bg-white/80 rounded-full p-2 text-black hover:bg-red-500 hover:text-white transition-colors"
          >
            ✕
          </button>
          
          {/* AR Camera Button (Dynamic Color) */}
          <button
            className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 text-white px-6 py-2 rounded-full font-bold shadow-md hover:opacity-90 flex items-center gap-2 transition-colors"
            style={{ backgroundColor: themeColor }}
            onClick={(e) => {
              e.stopPropagation();
              const model = modelRef.current;
              if (model && model.canActivateAR) model.activateAR();
            }}
          >
            <span>✨ AR View</span>
          </button>
        </>
      )}
    </div>
  );
};

export default FoodViewer;